/**
 * Gym Owner Dashboard — Supabase data layer.
 *
 * All queries for the org_pro gym owner dashboard.
 * No mock data — graceful empty states when tables are missing.
 */

import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';
import { captureError } from '@/lib/errorReporting';

const log = createLogger('GYM_OWNER');

/* ── Types ───────────────────────────────────────────────── */

export interface GymMember {
  id: string;
  name: string;
  handle: string;
  avatar_url: string | null;
  join_date: string;
  strength_score: number;
  last_active: string | null;
  freshness_band: string;
  workout_count_7d: number;
  workout_count_30d: number;
  total_volume_7d: number;
  subscription_tier: string;
}

export type MemberFilter = 'all' | 'active' | 'inactive' | 'new' | 'at_risk';

export interface EngagementStats {
  totalMembers: number;
  activeMembers30d: number;
  activeMembers7d: number;
  newMembers7d: number;
  atRiskMembers: number;
  workoutsThisWeek: number;
  workoutsThisMonth: number;
  avgStrengthScore: number;
  activePercentage: number;
  topPerformers: { name: string; handle: string; score: number }[];
  workoutDistribution: { bucket: string; count: number }[];
}

export interface GymAnnouncement {
  id: string;
  gym_id: string;
  author_id: string;
  author_name: string;
  text: string;
  created_at: string;
}

export interface GymChallengeInfo {
  id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  status: 'active' | 'upcoming' | 'completed';
  participant_count: number;
  metric: string;
  leaderboard: { name: string; handle: string; score: number; rank: number }[];
}

/* ── Member Queries ──────────────────────────────────────── */

/**
 * Fetch all gym members with workout stats.
 * Tries organization_memberships first, then falls back to profiles.home_gym_id/gym_id.
 */
export async function fetchGymMembers(
  gymId: string | null,
  orgId: string | null,
  gymName: string | null,
): Promise<{ members: GymMember[]; error: string | null }> {
  try {
    let profileRows: any[] = [];

    // Strategy 1: org memberships table
    if (orgId) {
      const { data: memberships, error: memErr } = await supabase
        .from('organization_memberships')
        .select('user_id, joined_at')
        .eq('org_id', orgId)
        .eq('status', 'active');

      if (!memErr && memberships && memberships.length > 0) {
        const userIds = memberships.map((m: any) => m.user_id);
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, handle, display_name, avatar_url, duel_standard_strength_kg, freshness_band, last_active, subscription_tier, created_at')
          .in('id', userIds);

        const joinMap = new Map(memberships.map((m: any) => [m.user_id, m.joined_at]));
        profileRows = (profiles || []).map((p: any) => ({
          ...p,
          join_date: joinMap.get(p.id) || p.created_at,
        }));
      }
    }

    // Strategy 2: profiles matched by gym_id
    if (profileRows.length === 0 && gymId) {
      const { data } = await supabase
        .from('profiles')
        .select('id, handle, display_name, avatar_url, duel_standard_strength_kg, freshness_band, last_active, subscription_tier, created_at')
        .or(`gym_id.eq.${gymId},home_gym_id.eq.${gymId}`)
        .not('handle', 'is', null)
        .order('duel_standard_strength_kg', { ascending: false, nullsFirst: false });

      profileRows = (data || []).map((p: any) => ({ ...p, join_date: p.created_at }));
    }

    // Strategy 3: match by gym name
    if (profileRows.length === 0 && gymName) {
      const { data } = await supabase
        .from('profiles')
        .select('id, handle, display_name, avatar_url, duel_standard_strength_kg, freshness_band, last_active, subscription_tier, created_at')
        .eq('home_gym_name', gymName)
        .not('handle', 'is', null)
        .order('duel_standard_strength_kg', { ascending: false, nullsFirst: false });

      profileRows = (data || []).map((p: any) => ({ ...p, join_date: p.created_at }));
    }

    if (profileRows.length === 0) {
      return { members: [], error: null };
    }

    // Fetch workout stats for all members
    const userIds = profileRows.map((p: any) => p.id);
    const now = Date.now();
    const d7 = new Date(now - 7 * 86400000).toISOString();
    const d30 = new Date(now - 30 * 86400000).toISOString();

    let workoutData7d: any[] = [];
    let workoutData30d: any[] = [];

    try {
      const { data: w7 } = await supabase
        .from('workouts')
        .select('user_id, payload')
        .in('user_id', userIds)
        .gte('performed_at', d7);
      workoutData7d = w7 || [];

      const { data: w30 } = await supabase
        .from('workouts')
        .select('user_id')
        .in('user_id', userIds)
        .gte('performed_at', d30);
      workoutData30d = w30 || [];
    } catch {
      // workouts table may not exist
    }

    // Count workouts per user
    const workout7dCount = new Map<string, number>();
    const volume7d = new Map<string, number>();
    const workout30dCount = new Map<string, number>();

    for (const w of workoutData7d) {
      workout7dCount.set(w.user_id, (workout7dCount.get(w.user_id) || 0) + 1);
      // Calculate volume
      const payload = typeof w.payload === 'string' ? JSON.parse(w.payload) : w.payload;
      let vol = 0;
      (payload?.exercises ?? []).forEach((ex: any) => {
        (ex.sets ?? []).forEach((s: any) => {
          vol += (Number(s.weight) || 0) * (Number(s.reps) || 0);
        });
      });
      volume7d.set(w.user_id, (volume7d.get(w.user_id) || 0) + vol);
    }

    for (const w of workoutData30d) {
      workout30dCount.set(w.user_id, (workout30dCount.get(w.user_id) || 0) + 1);
    }

    const members: GymMember[] = profileRows.map((p: any) => ({
      id: p.id,
      name: p.display_name || p.handle || 'Unknown',
      handle: p.handle ? `@${p.handle}` : '',
      avatar_url: p.avatar_url || null,
      join_date: (p.join_date || p.created_at || '').slice(0, 10),
      strength_score: Math.round(p.duel_standard_strength_kg ?? 0),
      last_active: p.last_active || null,
      freshness_band: (p.freshness_band ?? 'Unranked').toLowerCase(),
      workout_count_7d: workout7dCount.get(p.id) || 0,
      workout_count_30d: workout30dCount.get(p.id) || 0,
      total_volume_7d: volume7d.get(p.id) || 0,
      subscription_tier: p.subscription_tier || 'free',
    }));

    return { members, error: null };
  } catch (err: any) {
    captureError(err, { context: 'gymOwnerDashboard.fetchGymMembers' });
    return { members: [], error: err.message || 'Failed to load members' };
  }
}

/**
 * Filter members by category.
 */
export function filterMembers(members: GymMember[], filter: MemberFilter): GymMember[] {
  const now = Date.now();
  const d7 = now - 7 * 86400000;
  const d14 = now - 14 * 86400000;
  const d30 = now - 30 * 86400000;

  switch (filter) {
    case 'active':
      return members.filter(m => {
        if (!m.last_active) return false;
        return new Date(m.last_active).getTime() > d30;
      });
    case 'inactive':
      return members.filter(m => {
        if (!m.last_active) return true;
        return new Date(m.last_active).getTime() <= d30;
      });
    case 'new':
      return members.filter(m => {
        if (!m.join_date) return false;
        return new Date(m.join_date).getTime() > d7;
      });
    case 'at_risk':
      return members.filter(m => {
        if (!m.last_active) return true;
        const lastActive = new Date(m.last_active).getTime();
        return lastActive <= d14 && lastActive > d30;
      });
    default:
      return members;
  }
}

/* ── Engagement Analytics ────────────────────────────────── */

export function computeEngagementStats(members: GymMember[]): EngagementStats {
  const now = Date.now();
  const d7 = now - 7 * 86400000;
  const d14 = now - 14 * 86400000;
  const d30 = now - 30 * 86400000;

  const active30d = members.filter(m => m.last_active && new Date(m.last_active).getTime() > d30);
  const active7d = members.filter(m => m.last_active && new Date(m.last_active).getTime() > d7);
  const newMembers = members.filter(m => m.join_date && new Date(m.join_date).getTime() > d7);
  const atRisk = members.filter(m => {
    if (!m.last_active) return true;
    const t = new Date(m.last_active).getTime();
    return t <= d14;
  });

  const workoutsThisWeek = members.reduce((s, m) => s + m.workout_count_7d, 0);
  const workoutsThisMonth = members.reduce((s, m) => s + m.workout_count_30d, 0);

  const scores = members.map(m => m.strength_score).filter(s => s > 0);
  const avgStrength = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;

  // Top performers by weekly volume
  const topPerformers = [...members]
    .sort((a, b) => b.total_volume_7d - a.total_volume_7d)
    .slice(0, 5)
    .filter(m => m.total_volume_7d > 0)
    .map(m => ({ name: m.name, handle: m.handle, score: m.total_volume_7d }));

  // Workout frequency distribution
  const buckets = [
    { bucket: '0 workouts', min: 0, max: 0 },
    { bucket: '1-2 workouts', min: 1, max: 2 },
    { bucket: '3-4 workouts', min: 3, max: 4 },
    { bucket: '5+ workouts', min: 5, max: Infinity },
  ];
  const workoutDistribution = buckets.map(b => ({
    bucket: b.bucket,
    count: members.filter(m => m.workout_count_7d >= b.min && m.workout_count_7d <= b.max).length,
  }));

  return {
    totalMembers: members.length,
    activeMembers30d: active30d.length,
    activeMembers7d: active7d.length,
    newMembers7d: newMembers.length,
    atRiskMembers: atRisk.length,
    workoutsThisWeek,
    workoutsThisMonth,
    avgStrengthScore: avgStrength,
    activePercentage: members.length > 0 ? Math.round((active30d.length / members.length) * 100) : 0,
    topPerformers,
    workoutDistribution,
  };
}

/* ── Announcements ───────────────────────────────────────── */

/**
 * Fetch gym announcements. Tries Supabase table first, falls back to empty.
 */
export async function fetchAnnouncements(
  gymId: string,
): Promise<{ announcements: GymAnnouncement[]; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('gym_announcements')
      .select('id, gym_id, author_id, author_name, text, created_at')
      .eq('gym_id', gymId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      // Table may not exist
      const msg = String(error.message ?? '');
      if (error.code === '42P01' || /does not exist/i.test(msg)) {
        return { announcements: [], error: null };
      }
      return { announcements: [], error: error.message };
    }

    return { announcements: (data as GymAnnouncement[]) || [], error: null };
  } catch (err: any) {
    return { announcements: [], error: err.message };
  }
}

/**
 * Post a new announcement.
 */
export async function postAnnouncement(
  gymId: string,
  authorId: string,
  authorName: string,
  text: string,
): Promise<{ announcement: GymAnnouncement | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('gym_announcements')
      .insert({
        gym_id: gymId,
        author_id: authorId,
        author_name: authorName,
        text,
      })
      .select()
      .single();

    if (error) {
      const msg = String(error.message ?? '');
      if (error.code === '42P01' || /does not exist/i.test(msg)) {
        // Table doesn't exist — return a local-only announcement
        return {
          announcement: {
            id: `local_${Date.now()}`,
            gym_id: gymId,
            author_id: authorId,
            author_name: authorName,
            text,
            created_at: new Date().toISOString(),
          },
          error: null,
        };
      }
      return { announcement: null, error: error.message };
    }

    return { announcement: data as GymAnnouncement, error: null };
  } catch (err: any) {
    return { announcement: null, error: err.message };
  }
}

/* ── Member Management ───────────────────────────────────── */

/**
 * Toggle a member's active status in the gym.
 * Inactive members don't count toward gym battle rankings.
 */
export async function setMemberActive(
  gymId: string,
  userId: string,
  active: boolean,
): Promise<boolean> {
  const { error } = await supabase
    .from('gym_memberships')
    .update({ is_active: active })
    .eq('gym_id', gymId)
    .eq('user_id', userId);

  if (error) {
    log.warn('setMemberActive failed:', error.message);
    return false;
  }
  return true;
}

/**
 * Remove a member from the gym entirely (gym_memberships-first).
 */
export async function removeMemberFromGym(
  gymId: string,
  userId: string,
): Promise<boolean> {
  const { error } = await supabase
    .from('gym_memberships')
    .delete()
    .eq('gym_id', gymId)
    .eq('user_id', userId);

  if (error) {
    log.warn('removeMember failed:', error.message);
    // Fallback: update profile's home_gym_id if gym_memberships doesn't exist
    await supabase
      .from('profiles')
      .update({ home_gym_id: null, home_gym_name: null })
      .eq('id', userId)
      .eq('home_gym_id', gymId);
    return true;
  }
  return true;
}

/* ── Remove Member (legacy — org-aware) ─────────────────── */

export async function removeMember(
  orgId: string | null,
  gymId: string | null,
  userId: string,
): Promise<{ success: boolean; error: string | null }> {
  try {
    if (orgId) {
      const { error } = await supabase
        .from('organization_memberships')
        .update({ status: 'inactive' })
        .eq('org_id', orgId)
        .eq('user_id', userId);

      if (error) return { success: false, error: error.message };
      return { success: true, error: null };
    }

    // Fallback: remove gym association from profile
    if (gymId) {
      const { error } = await supabase
        .from('gym_memberships')
        .delete()
        .eq('gym_id', gymId)
        .eq('user_id', userId);

      if (error) {
        // Try clearing gym_id on profile
        await supabase
          .from('profiles')
          .update({ gym_id: null, home_gym_id: null })
          .eq('id', userId);
      }
      return { success: true, error: null };
    }

    return { success: false, error: 'No org or gym ID' };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

/* ── Gym Challenges ──────────────────────────────────────── */

export async function fetchGymChallenges(
  gymId: string,
): Promise<{ challenges: GymChallengeInfo[]; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('gym_challenges')
      .select('id, title, description, start_date, end_date, reward, gym_id')
      .eq('gym_id', gymId)
      .order('start_date', { ascending: false });

    if (error) {
      const msg = String(error.message ?? '');
      if (error.code === '42P01' || /does not exist/i.test(msg)) {
        return { challenges: [], error: null };
      }
      return { challenges: [], error: error.message };
    }

    const challengeRows = data || [];
    const now = new Date();

    // Get participant counts
    const challengeIds = challengeRows.map((c: any) => c.id);
    let participantMap = new Map<string, number>();
    if (challengeIds.length > 0) {
      try {
        const { data: participants } = await supabase
          .from('gym_challenge_participants')
          .select('challenge_id')
          .in('challenge_id', challengeIds);

        (participants || []).forEach((p: any) => {
          participantMap.set(p.challenge_id, (participantMap.get(p.challenge_id) || 0) + 1);
        });
      } catch {
        // participants table may not exist
      }
    }

    const challenges: GymChallengeInfo[] = challengeRows.map((c: any) => {
      const start = new Date(c.start_date);
      const end = new Date(c.end_date);
      let status: 'active' | 'upcoming' | 'completed' = 'upcoming';
      if (now >= start && now <= end) status = 'active';
      else if (now > end) status = 'completed';

      return {
        id: c.id,
        title: c.title,
        description: c.description || '',
        start_date: c.start_date,
        end_date: c.end_date,
        status,
        participant_count: participantMap.get(c.id) || 0,
        metric: c.reward || 'general',
        leaderboard: [],
      };
    });

    return { challenges, error: null };
  } catch (err: any) {
    return { challenges: [], error: err.message };
  }
}

/**
 * Create a gym challenge.
 */
export async function createGymChallenge(
  gymId: string,
  title: string,
  description: string,
  startDate: string,
  endDate: string,
): Promise<{ id: string | null; error: string | null }> {
  try {
    const { data, error } = await supabase
      .from('gym_challenges')
      .insert({
        gym_id: gymId,
        title,
        description,
        start_date: startDate,
        end_date: endDate,
      })
      .select('id')
      .single();

    if (error) {
      const msg = String(error.message ?? '');
      if (error.code === '42P01' || /does not exist/i.test(msg)) {
        return { id: null, error: 'gym_challenges table not found. Schema migration needed.' };
      }
      return { id: null, error: error.message };
    }

    return { id: data?.id || null, error: null };
  } catch (err: any) {
    return { id: null, error: err.message };
  }
}

/* ── Gym Battle ──────────────────────────────────────────── */

export interface GymBattleStats {
  myGym: {
    name: string;
    avg_strength: number;
    member_count: number;
    total_volume: number;
    rank: number;
  } | null;
  rivals: {
    gym_id: string;
    gym_name: string;
    avg_strength: number;
    member_count: number;
    rank: number;
  }[];
}

export async function fetchGymBattleStats(
  gymId: string,
  gymName: string,
): Promise<{ stats: GymBattleStats; error: string | null }> {
  try {
    // Fetch all profiles with gym + strength
    const { data, error } = await supabase
      .from('profiles')
      .select('gym_id, home_gym_name, duel_standard_strength_kg')
      .not('gym_id', 'is', null)
      .not('duel_standard_strength_kg', 'is', null)
      .gt('duel_standard_strength_kg', 0);

    if (error) return { stats: { myGym: null, rivals: [] }, error: error.message };
    if (!data || data.length === 0) return { stats: { myGym: null, rivals: [] }, error: null };

    // Collect all member scores per gym
    const gymMap = new Map<string, { name: string; scores: number[] }>();
    for (const row of data) {
      const gid = row.gym_id as string;
      const existing = gymMap.get(gid);
      if (existing) {
        existing.scores.push(row.duel_standard_strength_kg as number);
      } else {
        gymMap.set(gid, {
          name: (row.home_gym_name as string) || 'Unknown Gym',
          scores: [row.duel_standard_strength_kg as number],
        });
      }
    }

    // Score each gym by average of top 10 members (incentivizes inclusivity)
    const ranked = Array.from(gymMap.entries())
      .map(([id, g]) => {
        const sorted = g.scores.sort((a, b) => b - a);
        const topN = sorted.slice(0, 10);
        const avgScore = topN.reduce((sum, s) => sum + s, 0) / topN.length;
        return {
          gym_id: id,
          gym_name: g.name,
          avg_strength: Math.round(avgScore),
          member_count: g.scores.length, // total gym size
        };
      })
      .sort((a, b) => b.avg_strength - a.avg_strength)
      .map((g, i) => ({ ...g, rank: i + 1 }));

    const myGymData = ranked.find(g => g.gym_id === gymId);
    const rivals = ranked.filter(g => g.gym_id !== gymId).slice(0, 10);

    return {
      stats: {
        myGym: myGymData ? {
          name: myGymData.gym_name || gymName,
          avg_strength: myGymData.avg_strength,
          member_count: myGymData.member_count,
          total_volume: 0,
          rank: myGymData.rank,
        } : null,
        rivals,
      },
      error: null,
    };
  } catch (err: any) {
    return { stats: { myGym: null, rivals: [] }, error: err.message };
  }
}

/* ── TV Mode Data ────────────────────────────────────────── */

export interface TVModeBranding {
  logo_url: string | null;
  accent_color: string | null;
}

export interface TVModeData {
  leaderboard: { rank: number; name: string; handle: string; score: number }[];
  recentPRs: { name: string; handle: string; exercise: string; weight: number; date: string }[];
  activeChallenges: { title: string; participant_count: number; end_date: string }[];
  recentActivity: { name: string; handle: string; action: string; time: string }[];
  gymName: string;
  branding: TVModeBranding;
}

export async function fetchTVModeData(gymId: string): Promise<TVModeData> {
  const result: TVModeData = {
    leaderboard: [],
    recentPRs: [],
    activeChallenges: [],
    recentActivity: [],
    gymName: '',
    branding: { logo_url: null, accent_color: null },
  };

  try {
    // Fetch gym branding
    const { data: gymData } = await supabase
      .from('gyms')
      .select('name, logo_url, accent_color')
      .eq('id', gymId)
      .maybeSingle();
    if (gymData?.name) result.gymName = gymData.name;
    result.branding = {
      logo_url: (gymData as any)?.logo_url ?? null,
      accent_color: (gymData as any)?.accent_color ?? null,
    };

    // Leaderboard
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, handle, display_name, duel_standard_strength_kg')
      .or(`gym_id.eq.${gymId},home_gym_id.eq.${gymId}`)
      .not('handle', 'is', null)
      .order('duel_standard_strength_kg', { ascending: false, nullsFirst: false })
      .limit(100);

    result.leaderboard = (profiles || [])
      .filter((p: any) => (p.duel_standard_strength_kg || 0) > 0)
      .map((p: any, i: number) => ({
        rank: i + 1,
        name: p.display_name || p.handle || 'Athlete',
        handle: p.handle ? `@${p.handle}` : '',
        score: Math.round(p.duel_standard_strength_kg || 0),
      }));

    // Recent PRs from workouts
    const userIds = (profiles || []).map((p: any) => p.id);
    if (userIds.length > 0) {
      try {
        const d7 = new Date(Date.now() - 7 * 86400000).toISOString();
        const { data: recentWorkouts } = await supabase
          .from('workouts')
          .select('user_id, payload, performed_at')
          .in('user_id', userIds)
          .gte('performed_at', d7)
          .order('performed_at', { ascending: false })
          .limit(50);

        const profileMap = new Map((profiles || []).map((p: any) => [p.id, p]));
        const prs: TVModeData['recentPRs'] = [];

        for (const w of (recentWorkouts || [])) {
          const payload = typeof w.payload === 'string' ? JSON.parse(w.payload) : w.payload;
          const p = profileMap.get(w.user_id);
          for (const ex of (payload?.exercises ?? [])) {
            for (const set of (ex.sets ?? [])) {
              if (set.is_pr) {
                prs.push({
                  name: p?.display_name || p?.handle || 'Athlete',
                  handle: p?.handle ? `@${p.handle}` : '',
                  exercise: ex.name || 'Unknown',
                  weight: Number(set.weight) || 0,
                  date: w.performed_at,
                });
              }
            }
          }
        }
        result.recentPRs = prs.slice(0, 10);

        // Recent activity
        result.recentActivity = (recentWorkouts || []).slice(0, 10).map((w: any) => {
          const p = profileMap.get(w.user_id);
          const payload = typeof w.payload === 'string' ? JSON.parse(w.payload) : w.payload;
          const exCount = (payload?.exercises ?? []).length;
          return {
            name: p?.display_name || p?.handle || 'Athlete',
            handle: p?.handle ? `@${p.handle}` : '',
            action: `Logged ${exCount} exercise${exCount !== 1 ? 's' : ''}`,
            time: w.performed_at,
          };
        });
      } catch {
        // workouts table may not exist
      }
    }

    // Active challenges
    try {
      const now = new Date().toISOString();
      const { data: challenges } = await supabase
        .from('gym_challenges')
        .select('id, title, end_date')
        .eq('gym_id', gymId)
        .lte('start_date', now)
        .gte('end_date', now);

      if (challenges && challenges.length > 0) {
        const cIds = challenges.map((c: any) => c.id);
        const { data: participants } = await supabase
          .from('gym_challenge_participants')
          .select('challenge_id')
          .in('challenge_id', cIds);

        const pMap = new Map<string, number>();
        (participants || []).forEach((p: any) => {
          pMap.set(p.challenge_id, (pMap.get(p.challenge_id) || 0) + 1);
        });

        result.activeChallenges = challenges.map((c: any) => ({
          title: c.title,
          participant_count: pMap.get(c.id) || 0,
          end_date: c.end_date,
        }));
      }
    } catch {
      // challenges table may not exist
    }
  } catch (err) {
    log.warn('TV mode data fetch error:', err);
  }

  return result;
}

/* ── Member Engagement Heatmap ───────────────────────────── */

/**
 * Get member workout frequency by day of week and hour.
 * Returns a 7×24 grid of workout counts for heatmap rendering.
 */
export async function getMemberEngagementHeatmap(
  gymId: string,
  daysBack: number = 30,
): Promise<{ dayOfWeek: number; hour: number; count: number }[]> {
  const since = new Date(Date.now() - daysBack * 86400000).toISOString();

  // Get member user IDs first (workouts aren't always tagged with gym_id)
  const { data: members } = await supabase
    .from('profiles')
    .select('id')
    .or(`gym_id.eq.${gymId},home_gym_id.eq.${gymId}`);

  const memberIds = (members ?? []).map((m: any) => m.id);
  if (memberIds.length === 0) return [];

  const { data: workouts } = await supabase
    .from('workouts')
    .select('performed_at')
    .in('user_id', memberIds)
    .gte('performed_at', since);

  const grid: Record<string, number> = {};
  for (const w of workouts ?? []) {
    const d = new Date(w.performed_at);
    const key = `${d.getDay()}-${d.getHours()}`;
    grid[key] = (grid[key] ?? 0) + 1;
  }

  return Object.entries(grid).map(([key, count]) => {
    const [dayOfWeek, hour] = key.split('-').map(Number);
    return { dayOfWeek, hour, count };
  });
}

/* ── At-Risk Member Alerts ───────────────────────────────── */

/**
 * Check for at-risk members and notify the gym owner.
 * At-risk = hasn't logged a workout in 10+ days.
 * Call this from the notification scheduler cron.
 */
export async function notifyOwnerOfAtRiskMembers(
  gymId: string,
  ownerUserId: string,
): Promise<number> {
  const tenDaysAgo = new Date(Date.now() - 10 * 86400000).toISOString();

  // Get gym members who haven't worked out recently
  const { data: members } = await supabase
    .from('profiles')
    .select('id, handle, display_name, last_workout_at')
    .eq('home_gym_id', gymId)
    .lt('last_workout_at', tenDaysAgo);

  const atRisk = members ?? [];
  if (atRisk.length === 0) return 0;

  const names = atRisk.slice(0, 3).map(m => m.display_name ?? m.handle ?? 'Unknown');
  const extra = atRisk.length > 3 ? ` +${atRisk.length - 3} more` : '';

  await supabase.from('notifications').insert({
    user_id: ownerUserId,
    type: 'at_risk_members',
    title: `${atRisk.length} member${atRisk.length > 1 ? 's' : ''} going cold`,
    body: `${names.join(', ')}${extra} haven't trained in 10+ days. A quick message could bring them back.`,
    data: { type: 'at_risk_members', gymId, memberIds: atRisk.map(m => m.id) },
    is_read: false,
  });

  return atRisk.length;
}

/* ── Schema Documentation ────────────────────────────────── */

/**
 * Tables needed for full gym owner dashboard:
 *
 * EXISTING:
 * - profiles (standard user profiles)
 * - organizations (org records)
 * - organization_memberships (org ↔ user link)
 * - gyms (gym records)
 * - gym_memberships (gym ↔ user link)
 * - workouts (workout logs)
 * - gym_challenges (challenge definitions)
 * - gym_challenge_participants (challenge participation)
 *
 * NEEDED (for full feature set):
 * - gym_announcements: id, gym_id, author_id, author_name, text, created_at
 *   CREATE TABLE gym_announcements (
 *     id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
 *     gym_id uuid NOT NULL,
 *     author_id uuid NOT NULL,
 *     author_name text NOT NULL,
 *     text text NOT NULL,
 *     created_at timestamptz DEFAULT now()
 *   );
 */
